'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Key, LogOut } from 'lucide-react';
import { useAuth } from '@/lib/auth-context';
import { logout } from '@/lib/auth';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export function UserMenu() {
  const { user } = useAuth();
  const router = useRouter();

  if (!user) return null;

  const initials = user.name
    ? user.name.split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase()
    : user.email[0].toUpperCase();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted transition-colors">
          <Avatar className="h-7 w-7 shrink-0">
            <AvatarImage src={user.avatar_url || undefined} />
            <AvatarFallback className="text-[10px]">{initials}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 text-left hidden sm:block">
            <p className="text-sm font-medium leading-tight truncate flex items-center gap-1.5">
              {user.name || user.email}
              {user.is_superadmin && (
                <Badge variant="secondary" className="text-[9px] px-1 py-0 leading-tight">SA</Badge>
              )}
            </p>
          </div>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <div className="px-2 py-1.5">
          <p className="text-sm font-medium">{user.name || user.email}</p>
          {user.name && <p className="text-xs text-muted-foreground">{user.email}</p>}
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link href="/user/tokens">
            <Key className="h-4 w-4 mr-2" />
            API Tokens
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={async () => {
            await logout();
            router.push('/auth/login');
          }}
          className="text-destructive focus:text-destructive"
        >
          <LogOut className="h-4 w-4 mr-2" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
